import { Sparkles, ArrowRight, X, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * UpgradeModal — surfaced when a 402 Payment Required response comes
 * back from the backend. Handles two flavours of gate:
 *   - detail.code === "upgrade_required" — free-tier quota reached
 *   - detail.code === "trial_expired"    — trial has ended
 */
export default function UpgradeModal({ open, detail, onClose }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (!open || !detail) return null;

  const isTrial = detail.code === "trial_expired";

  const goPricing = () => {
    onClose?.();
    navigate("/pricing");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(20, 40, 30, 0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="upgrade-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl p-8 relative"
        style={{ background: "var(--bg-primary)", border: "1px solid var(--border-default)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          onClick={onClose}
          data-testid="upgrade-modal-close"
        >
          <X size={18}/>
        </button>

        <div className="flex items-center gap-2 label-eyebrow" style={{ color: "var(--accent-warm)" }}>
          {isTrial ? <Clock size={12}/> : <Sparkles size={12}/>}
          {isTrial ? t("trial.locked_title", { defaultValue: "Trial ended" }) : "Upgrade to Sprout Pro"}
        </div>
        <h2
          className="text-2xl font-medium tracking-tight mt-2"
          style={{ fontFamily: "var(--font-heading)" }}
          data-testid="upgrade-modal-title"
        >
          {isTrial
            ? t("trial.locked_headline")
            : <>You&rsquo;ve hit your free-tier limit on {detail.feature}.</>}
        </h2>
        <p className="mt-3 text-sm text-[var(--text-secondary)] leading-relaxed" data-testid="upgrade-modal-body">
          {isTrial ? (
            t("trial.locked_body")
          ) : (
            <>
              You&rsquo;ve used {detail.used} of {detail.limit} on the free plan. Upgrade to Pro
              for <strong>unlimited</strong> {detail.feature}, plus priority support and a 7-day free trial.
            </>
          )}
        </p>

        <button
          className="btn-primary w-full justify-center mt-6"
          onClick={goPricing}
          data-testid="upgrade-modal-cta"
        >
          {isTrial ? t("trial.locked_cta") : "See plans"} <ArrowRight size={15}/>
        </button>
        <button
          className="btn-ghost w-full justify-center mt-2 text-xs text-[var(--text-muted)]"
          onClick={onClose}
          data-testid="upgrade-modal-later"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
